/* Formulista newsletter banner: <fm-newsletter> validates the email field and submits the Shopify customer
   newsletter form without leaving the page. Shopify answers the post with a redirect back to the page, so the
   returned document is read for the success or error message it renders. Without JavaScript the form posts
   normally and the page shows the same message. */

(() => {
  if (customElements.get('fm-newsletter')) return;

  const L = window.FMListing;

  class FMNewsletter extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.form = this.querySelector('form');
      this.field = this.querySelector('.fm-nl__field');
      this.input = this.querySelector('.fm-nl__input');
      this.message = this.querySelector('.fm-nl__msg');
      this.text = this.querySelector('[data-fm-msg]');
      if (!this.form || !this.input) return;

      if (L && L.bindSeals) L.bindSeals(this, signal);

      this.form.addEventListener('submit', this.onSubmit, { signal });
      this.input.addEventListener(
        'input',
        () => {
          if (!this.field.classList.contains('is-invalid')) return;
          this.field.classList.remove('is-invalid');
          this.input.removeAttribute('aria-invalid');
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.request?.abort();
    }

    /** @param {string} text @param {'success' | 'error'} tone */
    show(text, tone) {
      this.message.hidden = false;
      this.message.classList.toggle('is-success', tone === 'success');
      this.message.classList.toggle('is-error', tone === 'error');
      this.message.style.animation = 'none';
      void this.message.offsetWidth;
      this.message.style.animation = '';
      this.text.textContent = text;
    }

    fail(text) {
      this.field.classList.remove('is-invalid');
      void this.field.offsetWidth;
      this.field.classList.add('is-invalid');
      this.input.setAttribute('aria-invalid', 'true');
      this.show(text, 'error');
      this.input.focus();
    }

    /** @param {SubmitEvent} event */
    onSubmit = async (event) => {
      if (!window.fetch) return;
      event.preventDefault();

      const value = this.input.value.trim();
      if (!value || !this.input.checkValidity()) {
        this.fail(this.dataset.invalid || this.input.validationMessage);
        return;
      }

      this.classList.add('is-sending');
      this.request?.abort();
      this.request = new AbortController();

      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: new FormData(this.form),
          headers: { Accept: 'text/html' },
          signal: this.request.signal,
        });
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const state = doc.querySelector(`#${CSS.escape(this.message.id)}`);
        const kind = state ? state.getAttribute('data-fm-news-state') : '';
        const message = state ? state.textContent.trim() : '';

        if (kind === 'error') {
          this.fail(message || this.dataset.invalid || '');
        } else if (kind === 'success' || response.ok) {
          this.input.value = '';
          this.show(message || this.text.textContent || '', 'success');
        } else {
          this.fail(this.dataset.invalid || '');
        }
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        // The request could not be made: let the browser post the form the ordinary way.
        this.form.removeEventListener('submit', this.onSubmit);
        this.form.submit();
      } finally {
        this.classList.remove('is-sending');
      }
    };
  }

  customElements.define('fm-newsletter', FMNewsletter);
})();
